import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Button, buttonClass } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { TextInput } from "../../components/ui/Field";
import { PageTitle, Spinner } from "../../components/ui/misc";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import { qk, useThemes } from "../../lib/queries";

export function ThemesAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: themes, isLoading } = useThemes();
  const { confirm, element } = useConfirm();
  const [name, setName] = useState("");
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.themes });

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const t = await api.themes.create(name.trim());
      await refresh();
      await navigate({ to: "/admin/themes/$themeId", params: { themeId: t.id } });
    } catch (err) {
      toast(`The theme could not be created: ${err instanceof Error ? err.message : "unknown error"}`, "error");
    }
  };

  return (
    <div>
      <PageTitle>Themes</PageTitle>
      <p className="mb-6 max-w-2xl text-ink-2">
        A theme is the house style: colours, fonts, sizes, cover, header and footer. Documents always export with the
        latest published version of their theme.
      </p>
      {isLoading ? <Spinner label="Loading themes…" /> : null}
      <ul className="mb-8 grid gap-2">
        {(themes ?? [])
          .filter((t) => !t.archived)
          .map((t) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium text-ink">
                  {t.name}
                  {t.isDefault ? (
                    <span className="rounded-full bg-green-wash px-2 py-px text-xs text-green-ink">Default</span>
                  ) : null}
                  {t.draft ? (
                    <span className="rounded-full bg-orange-wash px-2 py-px text-xs text-orange-ink">Draft open</span>
                  ) : null}
                </div>
                <div className="text-xs text-ink-3">
                  {t.published
                    ? `Version ${t.published.version} · ${t.published.layouts.map((l) => l.name).join(", ")}`
                    : "Not published yet"}
                </div>
              </div>
              <Link to="/admin/themes/$themeId" params={{ themeId: t.id }} className={buttonClass({ size: "sm" })}>
                Edit
              </Link>
              {!t.isDefault ? (
                <>
                  <Button
                    size="sm"
                    aria-label={`Make ${t.name} the default`}
                    onClick={async () => {
                      try {
                        await api.themes.update(t.id, { isDefault: true });
                      } catch (err) {
                        toast(
                          `${t.name} could not be made the default: ${err instanceof Error ? err.message : "unknown error"}`,
                          "error",
                        );
                      }
                      await refresh();
                    }}
                  >
                    Make default
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Archive ${t.name}?`,
                          body: "Documents that use it keep rendering with its last published version.",
                          confirmLabel: "Archive",
                          danger: true,
                        })
                      ) {
                        try {
                          await api.themes.update(t.id, { archived: true });
                        } catch (err) {
                          toast(
                            `${t.name} could not be archived: ${err instanceof Error ? err.message : "unknown error"}`,
                            "error",
                          );
                        }
                        await refresh();
                      }
                    }}
                  >
                    Archive
                  </Button>
                </>
              ) : null}
            </li>
          ))}
      </ul>
      {(themes ?? []).some((t) => t.archived) ? (
        <details className="mb-8 rounded-lg border border-line bg-surface px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium text-ink-2">Archived themes</summary>
          <ul className="mt-3 grid gap-1">
            {(themes ?? [])
              .filter((t) => t.archived)
              .map((t) => (
                <li key={t.id} className="flex items-center gap-3 rounded-md bg-column px-3 py-1.5 text-sm">
                  <span className="flex-1 text-ink">{t.name}</span>
                  <Button
                    size="sm"
                    aria-label={`Unarchive ${t.name}`}
                    onClick={async () => {
                      try {
                        await api.themes.update(t.id, { archived: false });
                        toast(`${t.name} is available again.`, "success");
                      } catch (err) {
                        toast(
                          `${t.name} could not be unarchived: ${err instanceof Error ? err.message : "unknown error"}`,
                          "error",
                        );
                      }
                      await refresh();
                    }}
                  >
                    Unarchive
                  </Button>
                </li>
              ))}
          </ul>
        </details>
      ) : null}
      <form onSubmit={create} className="flex max-w-md gap-2">
        <TextInput
          aria-label="New theme name"
          placeholder="New theme name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" variant="primary">
          Create theme
        </Button>
      </form>
      {element}
    </div>
  );
}
